import { Component, Input, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { PolicyTrackerData, PolicyStep } from "../policy-tracker.interfaces";
import { FlexLayoutModule } from "@angular/flex-layout";
import { Status } from "../policy-status.constants";

// Configuration for step status mapping
interface StepConfig {
  completedSteps: string[];
  declinedColor: 'red' | 'grey' | 'hidden';
}

@Component({
  selector: "app-policy-tracker-timeline",
  standalone: true,
  imports: [CommonModule, MatIconModule, FlexLayoutModule],
  templateUrl: "./policy-tracker-timeline.component.html",
  styleUrls: ["./policy-tracker-timeline.component.scss"],
})
export class PolicyTrackerTimelineComponent implements OnInit, OnChanges {
  @Input() policy!: PolicyTrackerData;
  @Input() status!: string;
  @Input() showWarning: boolean = false;
  
  private _processedSteps: PolicyStep[] = [];
  private _connectorClassCache: Map<string, string> = new Map();
  
  // Status configuration mapping
  private readonly statusConfig: Record<string, StepConfig> = {
    'SUBMITTED': {
      completedSteps: [Status.Submitted],
      declinedColor: 'grey'
    },
    'UNDER REVIEW': {
      completedSteps: [Status.Submitted, Status.UnderReview],
      declinedColor: 'grey'
    },
    'ACTIVE': {
      completedSteps: [Status.Submitted, Status.UnderReview, Status.Approved, Status.Active],
      declinedColor: 'grey'
    },
    'APPROVED': {
      completedSteps: [Status.Submitted, Status.UnderReview, Status.Approved],
      declinedColor: 'grey'
    },
    'REJECTED': {
      completedSteps: [Status.Submitted, Status.UnderReview],
      declinedColor: 'red'
    },
    'CANCELLED': {
      completedSteps: [Status.Submitted, Status.UnderReview],
      declinedColor: 'red'
    },
    'EXPIRED': {
      completedSteps: [Status.Submitted, Status.UnderReview],
      declinedColor: 'red'
    },
    'INACTIVE': {
      completedSteps: [Status.Submitted, Status.UnderReview],
      declinedColor: 'red'
    }
  };

  // Button configuration mapping
  private readonly buttonConfig: Record<string, { show: boolean; text: string }> = {
    'ACTIVE': { show: true, text: 'Active' },
    'APPROVED': { show: true, text: 'Approved' },
    'REJECTED': { show: false, text: 'Rejected' },
    'CANCELLED': { show: false, text: 'Cancelled' },
    'EXPIRED': { show: false, text: 'Expired' },
    'INACTIVE': { show: false, text: 'Inactive' }
  };

  ngOnInit() {
    this.updateProcessedSteps();
    this.policy.status = "SUBMITTED"; // Default status for testing
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['status'] || changes['policy']) {
      this._connectorClassCache.clear();
    }
  }

  updateProcessedSteps() {
    if (!this.policy?.statusTimeline) return;

    const timeline = this.policy.statusTimeline;
    this._processedSteps = [
      {
        id: Status.Submitted,
        title: "Submitted",
        date: timeline.submitted.date,
        status: timeline.submitted.completed,
        icon: "check",
      },
      {
        id: Status.UnderReview,
        title: "Under Review",
        date: timeline.underReview.date,
        status: timeline.underReview.completed,
        icon: "check",
      },
      {
        id: Status.Approved,
        title: "Approved",
        date: timeline.approved.date,
        status: timeline.approved.completed,
        icon: "check",
      },
      {
        id: Status.Active,
        title: "Active",
        date: timeline.active.date,
        status: timeline.active.completed,
        icon: "document",
      },
    ];
  }

  get processedSteps(): PolicyStep[] {
    return this._processedSteps;
  }

  get hasDeclined(): boolean {
    // Always show declined if it exists, regardless of status
    return this.policy?.statusTimeline?.[Status.Declined]?.completed === true;
  }

  get declinedStep(): PolicyStep | undefined {
    const declined = this.policy?.statusTimeline?.[Status.Declined];
    if (!declined?.completed) return undefined;
    return { 
      id: Status.Declined, 
      title: "Declined", 
      date: declined.date, 
      status: declined.completed, 
      icon: "cancel" 
    };
  }

  // Helper methods for status checking
  private currentStatus(): string {
    return this.status?.toUpperCase() || '';
  }

  private getStatusConfig(): StepConfig | undefined {
    return this.statusConfig[this.currentStatus()];
  }

  private isStepCompleted(stepId: string): boolean {
    const config = this.getStatusConfig();
    return config?.completedSteps.includes(stepId) || false;
  }

  private isDeclinedStatus(): boolean {
    const status = this.currentStatus();
    return ['REJECTED', 'CANCELLED', 'EXPIRED', 'INACTIVE'].includes(status);
  }

  private isApprovedOrActive(): boolean {
    const status = this.currentStatus();
    return status === 'APPROVED' || status === 'ACTIVE';
  }

  // Step class methods
  stepClass(step: PolicyStep): string {
    if (step.id === Status.Declined) {
      return this.declinedStepClass();
    }
    
    return this.isStepCompleted(step.id) 
      ? 'step-circle completed' 
      : 'step-circle greyed-out';
  }

  declinedStepClass(): string {
    const config = this.getStatusConfig();
    const declinedColor = config?.declinedColor || 'red';
    
    if (declinedColor === 'hidden') {
      return 'step-circle hidden';
    }
    
    return `step-circle ${declinedColor === 'red' ? 'declined' : 'greyed-out'}`;
  }

  declinedConnectorClass(): string {
    const config = this.getStatusConfig();
    const declinedColor = config?.declinedColor || 'red';
    
    if (declinedColor === 'hidden') {
      return 'hidden';
    }
    
    return declinedColor === 'red' ? '' : 'greyed-out';
  }

  // Connector class methods with caching
  connectorClass(step: PolicyStep, isLast: boolean): string {
    if (isLast) return '';
    
    const cacheKey = `${step.id}-${isLast}-${this.currentStatus()}-${this.hasDeclined}`;
    
    if (this._connectorClassCache.has(cacheKey)) {
      return this._connectorClassCache.get(cacheKey)!;
    }
    
    const status = this.currentStatus();
    const nextStep = this._processedSteps[this._processedSteps.indexOf(step) + 1];
    
    if (!nextStep) {
      const result = 'step-connector';
      this._connectorClassCache.set(cacheKey, result);
      return result;
    }
    
    // Check if both current and next steps are completed
    const isCompleted = this.isStepCompleted(step.id) && this.isStepCompleted(nextStep.id);
    
    let result: string;
    
    if (isCompleted) {
      // Special case for REJECTED status - half-red connector after Under Review
      if (status === 'REJECTED' && step.id === 'underReview' && nextStep.id === 'approved') {
        result = 'step-connector half-red';
      }
      // Special case for UNDER REVIEW status - half-red connector after Under Review if declined exists
      else if (status === 'UNDER REVIEW' && step.id === 'underReview' && nextStep.id === 'approved' && this.hasDeclined) {
        result = 'step-connector half-red';
      }
      else {
        result = 'step-connector completed';
      }
    } else if (status === "REJECTED" && step.id === "underReview" && nextStep.id === "approved") {
      result = "step-connector half-red";
    } else {
      result = "step-connector greyed-out";
    }
    
    this._connectorClassCache.set(cacheKey, result);
    return result;
  }

  // Line class methods
  lineClass(lineType: string): string {
    const status = this.currentStatus();
    
    const lineConfig: Record<string, string> = {
      "vertical-junction": this.isDeclinedStatus() ? "vertical-junction declined-red" : "vertical-junction",
      "branch-approved": this.isApprovedOrActive() ? "branch-approved" : "branch-approved greyed-out",
      "branch-declined": this.isDeclinedStatus() ? "branch-declined active-red" : "branch-declined greyed-out",
      "final-line": this.isApprovedOrActive() ? "line-approved-active" : "line-approved-active greyed-out"
    };
    
    return lineConfig[lineType] || lineType;
  }

  // Opacity methods
  stepOpacity(step: PolicyStep): number {
    const config = this.getStatusConfig();
    if (!config) return step.status ? 1 : 0.5;
    
    // All steps are fully opaque for most statuses
    return 1;
  }

  // Action button methods
  shouldShowApprovedButton(): boolean {
    const config = this.buttonConfig[this.currentStatus()];
    return config?.show || false;
  }

  shouldShowDeclinedButton(): boolean {
    return this.isDeclinedStatus();
  }

  approvedButtonText(): string {
    const config = this.buttonConfig[this.currentStatus()];
    return config?.text || 'Approved';
  }

  declinedButtonText(): string {
    const status = this.currentStatus();
    const declinedTextMap: Record<string, string> = {
      'REJECTED': 'Rejected',
      'CANCELLED': 'Cancelled',
      'EXPIRED': 'Expired',
      'INACTIVE': 'Inactive'
    };
    
    return declinedTextMap[status] || 'Declined';
  }

  // Utility methods
  isUnderReviewCompleted(): boolean {
    return this.policy?.statusTimeline?.underReview?.completed === true;
  }
}
